export enum SURVEY_ID {
  BMI_FIRST = 'bmi_first',
  PATIENT_CHOICE = 'patient_choice',
  DIGA_QUALIFIED = 'diga_qualified',
  PRESCRIPTION_UPLOAD = 'prescription_upload',
  BMI_FIRST_SIGNALING = 'bmi_first_signaling',
  BMI_FIRST_STORYTELLING = 'bmi_first_storytelling',
}

export interface SurveyConfig {
  themeName: string;
  locale: string;
}

export type SurveyConfigs = Record<SURVEY_ID, SurveyConfig>;
export const surveyConfigs: SurveyConfigs = {
  [SURVEY_ID.BMI_FIRST_SIGNALING]: {
    themeName: 'digaBMIFirstSignaling',
    locale: 'de',
  },
  [SURVEY_ID.BMI_FIRST_STORYTELLING]: {
    themeName: 'digaBMIFirstStorytelling',
    locale: 'de',
  },
  [SURVEY_ID.BMI_FIRST]: {
    themeName: 'digaBMIFirstBlue',
    locale: 'de',
  },
  [SURVEY_ID.PATIENT_CHOICE]: {
    themeName: 'patientChoice',
    locale: 'en',
  },
  [SURVEY_ID.DIGA_QUALIFIED]: {
    themeName: 'digaQualified',
    locale: 'de',
  },
  [SURVEY_ID.PRESCRIPTION_UPLOAD]: {
    themeName: 'digaQualified',
    locale: 'de',
  },
};

export enum DE_LP_EXPERIMENT_ID {
  A = 'A_Generic_CONTROL_132',
  B = 'B_Generic_Test_Doctor_Review_Component_132',
}

export enum DE_LP_PROCESS_EXPERIMENT_ID {
  A = 'A_Generic_CONTROL_138',
  B = 'B_Generic_ProcessLong_138',
  C = 'C_Generic_ProcessShort_138',
}

export enum DE_LP_STORY_TELLING_V1_2_EXPERIMENT_ID {
  A = 'A_Generic_CONTROL_197',
  B = 'B_Generic_STLP_197',
}

export enum DE_NATIVE_LP_KATJA_EXPERIMENT_ID {
  A = 'A_Native_Control_177',
  B = 'B_Native_Katja_177',
}

export enum DE_TV_MODEL_IMAGE_EXPERIMENT_ID {
  Holdout = 'Holdout_216',
  A = 'A_Generic_CONTROL_216',
  B = 'B_Generic_TVModelImageOnly_216',
}

export enum DE_SURVEY_PERFORMANCE_EXPERIMENT_ID {
  A = 'A_Generic_CONTROL_239',
  B = 'B_Generic_Next_239',
  Holdout = 'Holdout_239',
}

export enum DE_SUMMER_CAMPAIGN_EXPERIMENT_ID {
  A = 'A_Generic_CONTROL_252',
  B = 'B_Generic_SummerCampaign_252',
  Holdout = 'Holdout_252',
}

export type ABTestConfig = {
  variants: {
    id: string;
    percentage: number;
  }[];
  defaultVariantIndex: number;
  utmCampaignToEnable?: string;
  utmCampaignToDisable?: string;
  cookieName: string;
  variantParam?: string;
  gtmEventName?: string;
  experiment: string;
};

const DE_LP_EXPERIMENT_DEFAULT_CONFIG = {
  cookieName: 'oviva_experiment',
  variantParam: 'variant_id',
  gtmEventName: 'wordpress_experiment_track',
  defaultVariantIndex: 0,
};

export const BMI_FIRST_SURVEY_EXPERIMENT_PATH = {
  control: 'bmi_first',
  test: 'bmi_first',
};

export const EXPERIMENT_EVENT_NAME = 'wordpress_experiment_track';

export const DE_LP_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_LP_EXPERIMENT_ID.A, percentage: 50 },
    { id: DE_LP_EXPERIMENT_ID.B, percentage: 50 },
  ],
  experiment: 'paid_generic_132',
};

export const DE_LP_PROCESS_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_LP_PROCESS_EXPERIMENT_ID.A, percentage: 33.34 },
    { id: DE_LP_PROCESS_EXPERIMENT_ID.B, percentage: 33.33 },
    { id: DE_LP_PROCESS_EXPERIMENT_ID.C, percentage: 33.33 },
  ],
  experiment: 'paid_generic_138',
};

export const DE_LP_STORY_TELLING_V1_2_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_LP_STORY_TELLING_V1_2_EXPERIMENT_ID.A, percentage: 50 },
    { id: DE_LP_STORY_TELLING_V1_2_EXPERIMENT_ID.B, percentage: 50 },
  ],
  experiment: 'paid_generic_197',
};

export const DE_NATIVE_LP_KATJA_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_NATIVE_LP_KATJA_EXPERIMENT_ID.A, percentage: 50 },
    { id: DE_NATIVE_LP_KATJA_EXPERIMENT_ID.B, percentage: 50 },
  ],
  experiment: 'paid_native_177',
};

export const DE_TV_MODEL_IMAGE_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_TV_MODEL_IMAGE_EXPERIMENT_ID.A, percentage: 50 },
    { id: DE_TV_MODEL_IMAGE_EXPERIMENT_ID.B, percentage: 50 },
  ],
  experiment: 'paid_generic_216',
};

export const DE_SURVEY_PERFORMANCE_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_SURVEY_PERFORMANCE_EXPERIMENT_ID.A, percentage: 50 },
    { id: DE_SURVEY_PERFORMANCE_EXPERIMENT_ID.B, percentage: 50 },
    { id: DE_SURVEY_PERFORMANCE_EXPERIMENT_ID.Holdout, percentage: 0 },
  ],
  experiment: 'paid_generic_239',
};

export const DE_SUMMER_CAMPAIGN_EXPERIMENT: ABTestConfig = {
  ...DE_LP_EXPERIMENT_DEFAULT_CONFIG,
  variants: [
    { id: DE_SUMMER_CAMPAIGN_EXPERIMENT_ID.A, percentage: 50 },
    { id: DE_SUMMER_CAMPAIGN_EXPERIMENT_ID.B, percentage: 50 },
    { id: DE_SUMMER_CAMPAIGN_EXPERIMENT_ID.Holdout, percentage: 0 },
  ],
  experiment: 'paid_generic_252',
};

export const ACTIVE_EXPERIMENTS: ABTestConfig[] = [];

export const BMI_FIRST_ACTIVE_EXPERIMENTS: ABTestConfig[] = [];

export const BRAND_ACTIVE_EXPERIMENTS: ABTestConfig[] = [];

export const ACTIVE_NATIVE_KATJA_EXPERIMENTS = [DE_NATIVE_LP_KATJA_EXPERIMENT];

export const ACTIVE_NATIVE_EXPERIMENTS: ABTestConfig[] = [];

export const ACTIVE_META_EXPERIMENTS: ABTestConfig[] = [];
